import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import RegistryPanel from "@/components/admin/RegistryPanel";
import StudioPanel from "@/components/admin/StudioPanel";
import SentinelPanel from "@/components/admin/SentinelPanel";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";

const API = `${window.location.origin}/api`;

const TOKEN_KEY = "vdi_admin_token";

const STATUS_LABEL = {
  discovered: "🆕 Nuovo",
  enriched: "Arricchito",
  qualified: "Qualificato",
  queued: "In coda",
  sent: "Inviato",
  opened: "Aperto",
  clicked: "Cliccato",
  replied: "Risposto",
  opted_out: "Disiscritto",
  bounced: "Bounce",
  skipped: "Scartato",
  won: "🏆 Cliente acquisito",
};

const REGIONS = {
  Sicilia: ["Ragusa", "Modica", "Scicli", "Marina di Ragusa", "Vittoria", "Siracusa", "Ortigia", "Noto", "Marzamemi", "Avola", "Catania", "Taormina", "Cefalù", "Palermo", "Marsala", "Agrigento"],
  Calabria: ["Tropea", "Reggio Calabria", "Catanzaro", "Cosenza", "Diamante"],
  Puglia: ["Bari", "Lecce", "Polignano a Mare", "Ostuni", "Gallipoli", "Monopoli"],
  Campania: ["Napoli", "Sorrento", "Positano", "Amalfi", "Capri", "Ischia"],
  Lazio: ["Roma", "Ponza", "Sperlonga"],
  Toscana: ["Firenze", "Siena", "Forte dei Marmi", "Viareggio", "Montalcino"],
  Lombardia: ["Milano", "Como", "Bergamo", "Franciacorta"],
  Veneto: ["Venezia", "Verona", "Cortina d'Ampezzo", "Padova"],
  Piemonte: ["Torino", "Alba", "Barolo"],
  Liguria: ["Portofino", "Sanremo", "Genova", "Alassio"],
  "Emilia-Romagna": ["Bologna", "Modena", "Parma", "Rimini"],
  Sardegna: ["Porto Cervo", "Cagliari", "Alghero", "Olbia"],
  "🌍 Europa": ["Londra", "Parigi", "Monte Carlo", "Zurigo", "Ginevra", "Berlino", "Monaco di Baviera", "Amsterdam", "Bruxelles", "Vienna", "Madrid", "Barcellona", "Lisbona", "Copenaghen", "Stoccolma", "Praga"],
  "🌎 Americhe": ["New York", "Miami", "Los Angeles", "San Francisco", "Chicago", "Las Vegas", "Toronto", "Montreal", "Città del Messico", "San Paolo", "Buenos Aires"],
  "🌏 Asia & Oceania": ["Tokyo", "Osaka", "Singapore", "Hong Kong", "Shanghai", "Seoul", "Dubai", "Doha", "Bangkok", "Sydney", "Melbourne", "Auckland"],
};

const CATEGORIES = [
  { label: "Ristoranti", prefix: "Ristorante gourmet" },
  { label: "Stellati", prefix: "Ristorante stellato" },
  { label: "Hotel Luxury", prefix: "Luxury hotel" },
  { label: "Enoteche", prefix: "Enoteca" },
  { label: "Wine Bar", prefix: "Wine bar" },
  { label: "Fine Dining (Estero)", prefix: "fine dining restaurant" },
  { label: "Ristorante Italiano (Estero)", prefix: "italian fine dining restaurant" },
];

export default function OutreachAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("approvazione");
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ status: "", min_score: "", city: "", country: "" });
  const [visits, setVisits] = useState(null);

  const authHeader = { headers: { "X-Admin-Token": token } };

  const login = async () => {
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      setToken(data.token);
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Accesso riuscito");
    } catch (err) {
      setLoginError(
        err?.response?.status === 401
          ? "Password non valida. Controlli maiuscole, trattini e spazi."
          : "Errore di connessione. Riprovi tra qualche istante."
      );
    }
  };

  const logout = () => {
    setToken("");
    localStorage.removeItem(TOKEN_KEY);
    setConfig(null);
    setProspects([]);
    setSelected(null);
  };

  const changeCap = async () => {
    const v = window.prompt(
      "Nuovo limite giornaliero di email (1-200):\n\nConsiglio: oltre 50/giorno su un dominio giovane aumenta il rischio spam.",
      config?.daily_send_cap ?? 30
    );
    if (!v) return;
    const n = parseInt(v, 10);
    if (!n || n < 1 || n > 200) return toast.error("Valore non valido (1-200)");
    try {
      await axios.patch(`${API}/admin/outreach/config`, { daily_send_cap: n }, authHeader);
      toast.success(`Limite giornaliero aggiornato: ${n} email`);
      loadConfig();
    } catch {
      toast.error("Errore salvataggio limite");
    }
  };

  const loadConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/config`, authHeader);
      setConfig(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const loadProspects = async () => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.min_score) q.set("min_score", filter.min_score);
    if (filter.city) q.set("city", filter.city);
    if (filter.country) q.set("country", filter.country);
    q.set("limit", "2000");
    try {
      const { data } = await axios.get(`${API}/admin/outreach/prospects?${q}`, authHeader);
      setProspects(data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const loadVisits = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/visits`, authHeader);
      setVisits(data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (token) {
      loadConfig();
      loadProspects();
      loadVisits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) loadProspects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const call = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await loadProspects();
      await loadConfig();
      if (selected) {
        const { data } = await axios.get(
          `${API}/admin/outreach/prospects/${selected.id}`, authHeader
        );
        setSelected(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const runPipeline = (action) =>
    call(async () => {
      await axios.post(`${API}/admin/outreach/${action}/${selected.id}`, {}, authHeader);
      toast.success(`${action} completato`);
    });

  const generateDraft = (variant = "initial") =>
    call(async () => {
      await axios.post(
        `${API}/admin/outreach/generate/${selected.id}?variant=${variant}`,
        {}, authHeader
      );
      toast.success("Bozza generata");
    });

  const sendEmail = (dryRun, attachScheda = false) =>
    call(async () => {
      const { data } = await axios.post(
        `${API}/admin/outreach/send/${selected.id}`,
        { dry_run: dryRun, attach_scheda: attachScheda }, authHeader
      );
      if (data.dry_run) {
        toast.success(`DRY-RUN OK → ${data.would_send_to}`);
      } else {
        toast.success(`Inviato (${data.resend_id?.slice(0, 8)}…)`);
      }
    });

  const markReplied = () =>
    call(async () => {
      if (!window.confirm(`Confermi che "${selected.business_name}" ha RISPOSTO alla tua email?`)) return;
      await axios.post(`${API}/admin/outreach/prospects/${selected.id}/mark-replied`, {}, authHeader);
      toast.success("Segnato come Risposto");
    });

  const setStatus = (status) =>
    call(async () => {
      await axios.post(`${API}/admin/outreach/prospects/${selected.id}/set-status`, { status }, authHeader);
      toast.success(`Status corretto: ${STATUS_LABEL[status]}`);
    });

  const setManualEmail = (email) =>
    call(async () => {
      await axios.post(`${API}/admin/outreach/prospects/${selected.id}/set-email`, { email }, authHeader);
      toast.success("Email salvata — ora puoi inviare");
    });

  const discardSelected = () =>
    call(async () => {
      await axios.post(`${API}/admin/outreach/bulk-discard`, { ids: [selected.id] }, authHeader);
      toast.success("Prospect scartato");
    });

  const deleteSelected = () =>
    call(async () => {
      if (!window.confirm(`Eliminare definitivamente "${selected.business_name}"?`)) return;
      await axios.delete(`${API}/admin/outreach/prospects/${selected.id}`, authHeader);
      setSelected(null);
      toast.success("Prospect eliminato");
    });

  // ────────── Login screen ──────────
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#FDFBF7] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm border border-[#C4A671]/40 p-8 bg-[#111]"
        >
          <div className="overline text-[10px] text-[#C4A671] mb-3">
            Outreach Console
          </div>
          <h1 className="font-serif-display italic text-3xl mb-6">
            Accesso Riservato
          </h1>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Password amministratore"
            className="w-full bg-transparent border-b border-[#C4A671]/40 py-3
                       focus:border-[#C4A671] outline-none text-[#FDFBF7]
                       placeholder:text-[#7A7568] font-serif-display"
          />
          {loginError && (
            <p
              data-testid="admin-login-error"
              className="mt-3 text-[13px] text-[#E06C5A] font-serif-display"
            >
              {loginError}
            </p>
          )}
          <button
            data-testid="admin-login"
            onClick={login}
            className="w-full mt-6 bg-[#C4A671] text-[#0A0A0A] py-3
                       text-[10.5px] tracking-[0.28em] uppercase font-medium
                       hover:bg-[#FDFBF7] transition"
          >
            Entra →
          </button>
        </motion.div>
      </div>
    );
  }

  // ────────── Dashboard ──────────
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2EFE8]" data-testid="admin-dashboard">
      <header className="border-b border-[#1f1f1f] px-8 py-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[10px] text-[#C4A671]">Outreach Console</div>
          <h1 className="font-serif-display italic text-2xl mt-1">La Valle degli Iblei</h1>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div data-testid="visits-chip" className="text-center px-3 border-r border-[#1f1f1f]">
            <span className="overline text-[8.5px] text-[#7A7568] block">Visite sito</span>
            <span className="text-[15px] font-serif-display text-[#C4A671]">
              {visits ? `${visits.today} oggi · ${visits.total} tot` : "—"}
            </span>
          </div>
          <button
            data-testid="change-cap"
            onClick={changeCap}
            title="Cambia il limite giornaliero"
            className="text-center px-3 hover:opacity-80 transition"
          >
            <span className="overline text-[8.5px] text-[#7A7568] block">Inviate 24h ✎</span>
            <span className="text-[15px] font-serif-display text-[#C4A671]">
              {config?.sent_today ?? 0}/{config?.daily_send_cap ?? "—"}
            </span>
          </button>
          <StatusChip label="Prospect" value={config?.total_prospects ?? 0} />
          <button
            data-testid="bonifica-globale"
            title="STEP 2.0: pulizia qualità (filtro 65 + fuori target → Archivio), rigenerazione massiva delle prime email col nuovo prompt e ri-allineamento bozze follow-up per le fasce 👀/❄️. I 🔥 Cliccati restano esclusi. Nessuna email viene inviata: solo bozze."
            onClick={async () => {
              try {
                const { data: st } = await axios.get(`${API}/admin/outreach/bonifica/status`, authHeader);
                if (st.running) {
                  toast.info(`Bonifica già in corso — fase ${st.phase}: ${st.done ?? 0} di ${st.total ?? 0}`);
                  return;
                }
                const { data: pv } = await axios.get(`${API}/admin/outreach/bonifica/preview`, authHeader);
                const ok = window.confirm(
                  `🧹 BONIFICA GLOBALE — ANTEPRIMA DEL LOTTO\n\n` +
                  `1. Pulizia qualità su ${pv.pre_send} anagrafiche pre-invio (filtro 65 + fuori target → Archivio)\n` +
                  `2. Rigenerazione di ~${pv.queued_drafts} prime email col NUOVO prompt (Approvazione Veloce)\n` +
                  `3. Bozze follow-up: ${pv.warm} 👀 Aperti (sollecito 8gg) + ${pv.cold} ❄️ In attesa (rilancio 15gg)\n` +
                  `   🔥 Cliccati esclusi: ${pv.clicked}\n\n` +
                  `NESSUNA EMAIL VIENE INVIATA: vengono solo rigenerate le bozze.\n` +
                  `L'operazione userà l'AI e può durare a lungo. Confermi l'avvio?`
                );
                if (!ok) return;
                await axios.post(`${API}/admin/outreach/bonifica/start`, {}, authHeader);
                toast.success("🧹 Bonifica globale avviata in background — segui l'avanzamento e il resoconto qui nel pulsante");
              } catch { toast.error("Impossibile avviare la bonifica"); }
            }}
            className="overline text-[9.5px] px-3 py-2 border border-[#C4A671]/60 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
          >
            🧹 Bonifica Globale
          </button>
          <button
            data-testid="auto-hunt-toggle"
            title="Ricerche automatiche giornaliere (caccia email notturna + ciclo mattutino Autopilota) che usano Google a pagamento: spegnile quando vuoi per azzerare le spese. Le azioni manuali restano sempre disponibili."
            onClick={async () => {
              const next = !(config?.auto_hunt_enabled !== false);
              try {
                await axios.patch(`${API}/admin/outreach/config`, { auto_hunt_enabled: next }, authHeader);
                toast.success(next
                  ? "Ricerche automatiche ATTIVE 🟢 — caccia notturna e ciclo mattutino ripartiranno da soli"
                  : "Ricerche automatiche SPENTE 🔴 — zero spese Google finché non le riaccendi");
                loadConfig();
              } catch { toast.error("Cambio non riuscito"); }
            }}
            className={`overline text-[9.5px] px-3 py-2 border transition ${
              config?.auto_hunt_enabled !== false
                ? "border-[#3f7a4f]/60 text-[#7fc98f] hover:bg-[#3f7a4f]/20"
                : "border-[#c0392b]/60 text-[#e07b6d] hover:bg-[#c0392b]/20"
            }`}
          >
            {config?.auto_hunt_enabled !== false ? "🟢 Ricerche auto ON" : "🔴 Ricerche auto OFF"}
          </button>
          <button
            data-testid="system-reset"
            title="Se qualcosa si blocca (Autopilota, Sentinella, caccia email, batch): sblocca tutto senza perdere i progressi"
            onClick={async () => {
              if (!window.confirm("⟳ RESET GENERALE\n\nSblocca tutti i motori eventualmente congelati (Autopilota, Sentinella, caccia email, batch giornaliero).\nNessun dato o progresso viene perso: bozze, prospect e cronologia restano intatti.\n\nProcedere?")) return;
              try {
                const { data } = await axios.post(`${API}/admin/outreach/system/reset`, {}, authHeader);
                toast.success(data.total
                  ? `Sistema sbloccato ✅ (${data.total} motori resettati) — puoi far ripartire tutto.`
                  : "Nessun blocco rilevato: tutti i motori erano già liberi ✅");
              } catch { toast.error("Reset non riuscito."); }
            }}
            className="overline text-[9.5px] border border-[#c0392b]/50 text-[#e07b6d] px-3 py-2 hover:bg-[#c0392b] hover:text-white transition"
          >
            ⟳ Reset
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="overline text-[9.5px] text-[#7A7568] hover:text-[#C4A671] transition ml-2"
          >
            Esci
          </button>
        </div>
      </header>

      <nav className="border-b border-[#1f1f1f] px-8 flex gap-1">
        {[
          ["approvazione", "✓ Approvazione Veloce"],
          ["inviate", "✉ Inviate"],
          ["followup", "↻ Follow-up"],
          ["manuale", "🔎 Ricerca Manuale"],
          ["ricerca", "◎ Ricerca"],
          ["archivio", "≡ Archivio"],
          ["mappa", "🗺 Mappa"],
          ["registro", "✦ Registro"],
          ["studio", "✎ Studio"],
          ["sentinel", "🎯 Sentinel"],
          ["analytics", "📊 Analytics"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-5 py-3.5 text-[10.5px] tracking-[0.22em] uppercase transition border-b-2 -mb-px ${
              tab === key
                ? "border-[#C4A671] text-[#C4A671]"
                : "border-transparent text-[#7A7568] hover:text-[#C9C3B5]"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      <GlobalProgressBar authHeader={authHeader} onIdle={() => { loadProspects(); loadConfig(); }} />

      <main className="p-8">
        {tab === "approvazione" && (
          <ApprovalPanel authHeader={authHeader} onChanged={() => { loadProspects(); loadConfig(); }} />
        )}
        {tab === "inviate" && <SentPanel authHeader={authHeader} />}
        {tab === "followup" && <FollowupPanel authHeader={authHeader} />}
        {tab === "manuale" && <ManualSearchPanel authHeader={authHeader} />}
        {tab === "ricerca" && (
          <SearchPanel authHeader={authHeader} onChanged={() => { loadProspects(); loadConfig(); }} />
        )}
        {tab === "archivio" && (
          <ArchivePanel
            prospects={prospects}
            filter={filter}
            setFilter={setFilter}
            selected={selected}
            setSelected={setSelected}
            authHeader={authHeader}
            busy={busy}
            runPipeline={runPipeline}
            generateDraft={generateDraft}
            sendEmail={sendEmail}
            discardSelected={discardSelected}
            deleteSelected={deleteSelected}
            markReplied={markReplied}
            setStatus={setStatus}
            setManualEmail={setManualEmail}
            onChanged={loadProspects}
          />
        )}
        {tab === "mappa" && <MapPanel authHeader={authHeader} />}
        {tab === "registro" && <RegistryPanel authHeader={authHeader} />}
        {tab === "studio" && <StudioPanel authHeader={authHeader} />}
        {tab === "sentinel" && <SentinelPanel authHeader={authHeader} />}
        {tab === "analytics" && <AnalyticsPanel authHeader={authHeader} />}
      </main>
    </div>
  );
}

// ══════════════════ MAPPA: gestione località ══════════════════
function MapPanel({ authHeader }) {
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState("");
  const [halo, setHalo] = useState(16);
  const [busy, setBusy] = useState(false);
  const dragId = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/map/locations`);
      setLocations(data?.locations || []);
    } catch {
      toast.error("Errore caricamento località.");
    }
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (id) => () => { dragId.current = id; };
  const onDragEnter = (id) => (e) => {
    e.preventDefault();
    if (!dragId.current || dragId.current === id) return;
    setLocations((prev) => {
      const arr = [...prev];
      const from = arr.findIndex((l) => l.id === dragId.current);
      const to = arr.findIndex((l) => l.id === id);
      if (from < 0 || to < 0) return prev;
      arr.splice(to, 0, arr.splice(from, 1)[0]);
      return arr;
    });
  };
  const onDragEnd = () => {
    if (!dragId.current) return;
    dragId.current = null;
    setLocations((prev) => {
      axios
        .put(`${API}/admin/outreach/map-locations/reorder`, { ids: prev.map((l) => l.id) }, authHeader)
        .then(() => toast.success("Ordine salvato."))
        .catch(() => { toast.error("Errore salvataggio ordine."); load(); });
      return prev;
    });
  };

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/outreach/map-locations`,
        { name: name.trim(), halo },
        authHeader
      );
      toast.success(`"${data.label}" aggiunta alla mappa (${data.resolved || "coordinate trovate"}).`);
      setName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiunta località.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Rimuovere "${loc.label}" dalla mappa?`)) return;
    try {
      await axios.delete(`${API}/admin/outreach/map-locations/${loc.id}`, authHeader);
      toast.success(`"${loc.label}" rimossa.`);
      setLocations((p) => p.filter((l) => l.id !== loc.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rimozione.");
    }
  };

  return (
    <div className="max-w-3xl" data-testid="map-panel">
      <h2 className="font-serif-display text-[22px] text-[#F2EFE8] mb-1">
        Località della Mappa
      </h2>
      <p className="text-[13px] text-[#7A7568] mb-6">
        Ogni località genera automaticamente il raggio e l&apos;alone che partono da Ragusa
        sulla mappa del sito. Le coordinate vengono calcolate da Google in automatico.
      </p>

      <div className="flex items-end gap-3 mb-8 flex-wrap">
        <label className="flex-1 min-w-[220px]">
          <span className="overline text-[9px] text-[#7A7568] block mb-1.5">
            Nuova città / area (es. "Tokyo", "Zona Taormina", "Londra")
          </span>
          <input
            data-testid="map-add-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="w-full bg-[#111] border border-[#2a2a2a] px-4 py-2.5 text-[14px] text-[#F2EFE8] outline-none focus:border-[#C4A671]"
            placeholder="Digita la località…"
          />
        </label>
        <label>
          <span className="overline text-[9px] text-[#7A7568] block mb-1.5">Alone</span>
          <select
            data-testid="map-add-halo"
            value={halo}
            onChange={(e) => setHalo(Number(e.target.value))}
            className="bg-[#111] border border-[#2a2a2a] px-3 py-2.5 text-[13px] text-[#F2EFE8] outline-none"
          >
            <option value={12}>Piccolo</option>
            <option value={16}>Medio</option>
            <option value={20}>Grande</option>
          </select>
        </label>
        <button
          data-testid="map-add-btn"
          onClick={add}
          disabled={busy || !name.trim()}
          className="px-6 py-2.5 bg-[#C4A671] text-[#0A0A0A] text-[10.5px] tracking-[0.22em] uppercase font-medium hover:opacity-90 transition disabled:opacity-40"
        >
          {busy ? "Geocodifica…" : "+ Aggiungi"}
        </button>
      </div>

      <div className="border border-[#1f1f1f] divide-y divide-[#1f1f1f]" data-testid="map-locations-list">
        {locations.map((loc) => (
          <div
            key={loc.id}
            draggable
            onDragStart={onDragStart(loc.id)}
            onDragEnter={onDragEnter(loc.id)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={onDragEnd}
            className="flex items-center gap-4 px-4 py-3 hover:bg-[#111] transition cursor-grab active:cursor-grabbing"
            data-testid={`map-row-${loc.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-[#4a4a4a] select-none text-[14px] leading-none" title="Trascina per riordinare">⠿</span>
            <span className="w-2 h-2 rounded-full bg-[#C4A671] shrink-0" />
            <div className="flex-1">
              <span className="text-[14px] text-[#F2EFE8] font-serif-display">{loc.label}</span>
              <span className="text-[11px] text-[#7A7568] ml-3">
                {loc.lat?.toFixed?.(2)}, {loc.lon?.toFixed?.(2)}
                {loc.resolved ? ` · ${loc.resolved}` : ""}
              </span>
            </div>
            <span className="overline text-[8.5px] text-[#7A7568]">
              alone {loc.halo <= 12 ? "S" : loc.halo <= 16 ? "M" : "L"}
            </span>
            <button
              data-testid={`map-remove-${loc.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => remove(loc)}
              className="text-[#7A7568] hover:text-[#e05555] transition text-[16px] px-2"
              title="Rimuovi dalla mappa"
            >
              ✕
            </button>
          </div>
        ))}
        {locations.length === 0 && (
          <div className="px-4 py-8 text-center text-[13px] text-[#7A7568]">
            Nessuna località — aggiungine una qui sopra.
          </div>
        )}
      </div>
      <p className="overline text-[8.5px] text-[#7A7568] mt-4">
        {locations.length} località attive · Trascina ⠿ per riordinare le linee · Le modifiche sono immediate sul sito
      </p>
    </div>
  );
}
function ApprovalPanel({ authHeader, onChanged }) {
  const [state, setState] = useState({});
  const [queued, setQueued] = useState([]);
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = async (preselect = false) => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/daily-batch/status`, authHeader);
      setState(data.state || {});
      setQueued(data.queued || []);
      if (preselect || Object.keys(checked).length === 0) {
        const pre = {};
        (data.queued || []).slice(0, 10).forEach((p) => { pre[p.id] = true; });
        setChecked(pre);
      }
      return data.state || {};
    } catch { return {}; }
  };

  useEffect(() => {
    (async () => {
      const s = await loadStatus(true);
      if (s && s.running) startPolling();
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await loadStatus(true);
      if (s && s.running === false) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        toast.success("Preparazione completata!");
        onChanged();
      }
    }, 4000);
  };

  const stopBatch = async () => {
    try {
      await axios.post(`${API}/admin/outreach/daily-batch/stop`, {}, authHeader);
      toast.success("Stop richiesto — il batch si ferma al prospect corrente");
      setTimeout(() => loadStatus(true), 3000);
    } catch {
      toast.error("Errore stop");
    }
  };

  const selectedIds = Object.keys(checked).filter((id) => checked[id]);

  const bulkSend = async () => {
    if (!selectedIds.length) return toast.error("Nessun prospect selezionato");
    if (!window.confirm(`Confermi l'INVIO REALE di ${selectedIds.length} email?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/bulk-send`, { ids: selectedIds }, authHeader);
      toast.success(`Inviate: ${data.sent.length} · Fallite: ${data.failed.length}`);
      if (data.failed.length) {
        data.failed.slice(0, 3).forEach((f) => toast.error(`${f.id.slice(0, 8)}: ${f.reason}`));
      }
      setChecked({});
      await loadStatus(true);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    } finally {
      setBusy(false);
    }
  };

  const bulkDiscard = async () => {
    if (!selectedIds.length) return toast.error("Nessun prospect selezionato");
    if (!window.confirm(`Scartare ${selectedIds.length} prospect selezionati?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/bulk-discard`, { ids: selectedIds }, authHeader);
      toast.success(`Scartati: ${data.discarded}`);
      setChecked({});
      await loadStatus(true);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const running = state.running === true;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-serif-display italic text-2xl">I migliori del giorno</h2>
          <p className="text-[13px] text-[#8B7A55] mt-1">
            L'AI arricchisce, qualifica e prepara le bozze. Tu approvi con un click.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="refresh-batch"
            onClick={() => { loadStatus(true); toast.success("Aggiornato"); }}
            className="border border-[#2a2a2a] text-[#C9C3B5] px-4 py-3 text-[10.5px] tracking-[0.2em]
                       uppercase hover:border-[#C4A671] hover:text-[#C4A671] transition"
          >
            ⟳ Aggiorna
          </button>
          {running && (
            <button
              data-testid="stop-daily-batch"
              onClick={stopBatch}
              className="border border-[#E06C5A] text-[#E06C5A] px-4 py-3 text-[10.5px] tracking-[0.2em]
                         uppercase hover:bg-[#E06C5A] hover:text-[#0A0A0A] transition"
            >
              ⏹ Ferma
            </button>
          )}
        </div>
      </div>

      {running && (
        <div data-testid="batch-progress" className="border border-[#C4A671]/30 bg-[#111] p-4 mb-6 text-[13px]">
          <span className="text-[#C4A671]">L'AI sta lavorando…</span>{" "}
          Pronti: <b>{state.prepared ?? 0}/{state.target ?? 10}</b> · Analizzati: {state.examined ?? 0}
          {state.last_name && <> · In esame: <i>{state.last_name}</i></>}
        </div>
      )}

      {queued.length === 0 && !running ? (
        <div className="border border-[#1f1f1f] p-10 text-center text-[#7A7568] text-sm">
          Nessuna bozza in coda. Vai in <b className="text-[#C4A671]">Archivio</b>, seleziona i contatti con le checkbox e premi "⚡ Genera email per selezionati".
        </div>
      ) : (
        <>
          <table className="w-full text-[13.5px]" data-testid="approval-table">
            <thead>
              <tr className="text-left overline text-[9px] text-[#8B7A55] border-b border-[#1f1f1f]">
                <th className="py-3 pr-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="check-all"
                    checked={queued.length > 0 && queued.every((p) => checked[p.id])}
                    onChange={(e) => {
                      const all = {};
                      queued.forEach((p) => { all[p.id] = e.target.checked; });
                      setChecked(all);
                    }}
                    className="accent-[#C4A671] w-4 h-4"
                  />
                </th>
                <th className="py-3 pr-4">Nome</th>
                <th className="py-3 pr-4">Città</th>
                <th className="py-3 pr-4">Fit</th>
                <th className="py-3">Anteprima bozza</th>
              </tr>
            </thead>
            <tbody>
              {queued.map((p) => (
                <React.Fragment key={p.id}>
                <tr
                  data-testid={`approval-row-${p.id}`}
                  className="border-b border-[#161616] hover:bg-[#111] transition align-top"
                >
                  <td className="py-3.5 pr-3">
                    <input
                      type="checkbox"
                      data-testid={`check-${p.id}`}
                      checked={!!checked[p.id]}
                      onChange={(e) => setChecked({ ...checked, [p.id]: e.target.checked })}
                      className="accent-[#C4A671] w-4 h-4"
                    />
                  </td>
                  <td className="py-3.5 pr-4">
                    <div className="font-serif-display text-[15px]">{p.business_name}</div>
                    <div className="text-[11px] text-[#7A7568]">{p.primary_email}</div>
                  </td>
                  <td className="py-3.5 pr-4 text-[#C9C3B5]">{p.city || "—"}</td>
                  <td className="py-3.5 pr-4">
                    <span className={`font-medium ${(p.fit_score ?? 0) >= 80 ? "text-[#7CB87C]" : "text-[#C4A671]"}`}>
                      {p.fit_score ?? "—"}%
                    </span>
                  </td>
                  <td className="py-3.5 text-[#C9C3B5]">
                    <div className="italic text-[#F2EFE8]">{p.current_draft?.subject}</div>
                    <div
                      data-testid={`preview-${p.id}`}
                      onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                      className="cursor-pointer mt-1 text-[12.5px] leading-relaxed"
                    >
                      {(p.current_draft?.text || "").slice(0, 140)}…{" "}
                      <span className="text-[#C4A671]">{expanded === p.id ? "[chiudi editor]" : "[✎ apri e rifinisci]"}</span>
                    </div>
                  </td>
                </tr>
                {expanded === p.id && (
                  <tr className="border-b border-[#161616] bg-[#0d0d0d]">
                    <td colSpan={5} className="py-4 px-2">
                      <DraftEditor
                        prospectId={p.id}
                        initialSubject={p.current_draft?.subject || ""}
                        initialText={p.current_draft?.text || ""}
                        authHeader={authHeader}
                        onUpdated={(draft) => {
                          setQueued((prev) => prev.map((q) =>
                            q.id === p.id ? { ...q, current_draft: { ...q.current_draft, ...draft } } : q));
                        }}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="flex flex-wrap gap-3 mt-6">
            <button
              data-testid="bulk-send"
              onClick={bulkSend}
              disabled={busy || !selectedIds.length}
              className="bg-[#63171B] text-white px-6 py-3.5 text-[10.5px] tracking-[0.24em]
                         uppercase font-medium hover:bg-white hover:text-[#63171B] transition disabled:opacity-40"
            >
              ✉ Approva e invia selezionati ({selectedIds.length})
            </button>
            <button
              data-testid="bulk-discard"
              onClick={bulkDiscard}
              disabled={busy || !selectedIds.length}
              className="border border-[#7A7568] text-[#C9C3B5] px-6 py-3.5 text-[10.5px]
                         tracking-[0.24em] uppercase hover:border-[#E06C5A] hover:text-[#E06C5A]
                         transition disabled:opacity-40"
            >
              ✕ Scarta selezionati
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════ POSTA INVIATA ══════════════════
function SentPanel({ authHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [view, setView] = useState("tutte");
  const [lastSync, setLastSync] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/sent`, authHeader);
      setItems(data);
      setLastSync(new Date());
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // sync live ogni 30s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewEmail = async (resendId) => {
    if (openId === resendId) { setOpenId(null); setContent(null); return; }
    setOpenId(resendId);
    setContent(null);
    setLoadingContent(true);
    try {
      const { data } = await axios.get(`${API}/admin/outreach/sent/${resendId}/content`, authHeader);
      setContent(data);
    } catch {
      toast.error("Contenuto non recuperabile");
      setOpenId(null);
    }
    setLoadingContent(false);
  };

  const filtered = items.filter((it) =>
    view === "aperte" ? it.opened_at || it.clicked_at
    : view === "cliccate" ? it.clicked_at
    : true
  );

  return (
    <div className="max-w-5xl" data-testid="sent-panel">
      <h2 className="font-serif-display italic text-2xl mb-1">Posta inviata</h2>
      <p className="text-[13px] text-[#8B7A55] mb-5">
        Solo le email realmente partite verso i ristoranti. Clicca "Vedi email" per leggere il testo esatto.
      </p>
      <div className="flex gap-2 mb-5 items-center flex-wrap">
        {[["tutte", "Tutte"], ["aperte", "👀 Solo aperte"], ["cliccate", "🔥 Solo cliccate"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`sent-filter-${k}`}
            onClick={() => setView(k)}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.16em] border transition ${
              view === k
                ? "border-[#C4A671] bg-[#C4A671] text-[#0A0A0A]"
                : "border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          data-testid="sent-refresh"
          onClick={load}
          className="px-4 py-2 text-[11px] uppercase tracking-[0.16em] border border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60 transition"
        >
          ⟳ Aggiorna
        </button>
        {lastSync && (
          <span className="text-[10px] text-[#7A7568]">
            sync {lastSync.toLocaleTimeString("it-IT")} · auto ogni 30s
          </span>
        )}
      </div>
      {loading ? (
        <div className="text-[#7A7568] text-sm py-8">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-[#1f1f1f] p-10 text-center text-[#7A7568] text-sm">
          {view === "tutte" ? "Nessuna email inviata finora."
            : view === "aperte" ? "Nessuna email aperta finora."
            : "Nessun click finora."}
        </div>
      ) : (
        <div className="space-y-1">
          <div className="text-[12px] text-[#C4A671] mb-3">{filtered.length} email</div>
          {filtered.map((it) => (
            <div key={it.resend_id} className="border border-[#161616] bg-[#0d0d0d]">
              <div className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif-display text-[15.5px]">{it.business_name}</div>
                  <div className="text-[12px] text-[#7A7568] mt-0.5">
                    {it.city || "—"} · a: {it.to || "—"} ·{" "}
                    {it.sent_at ? new Date(it.sent_at).toLocaleString("it-IT") : "—"}
                  </div>
                  <div className="text-[13px] italic text-[#C9C3B5] mt-1">«{it.subject}»</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] text-[#7A7568]">
                    {it.clicked_at ? "🔥 cliccata" : it.opened_at ? "👀 aperta" : "· in attesa"}
                  </span>
                  <button
                    data-testid={`view-sent-${it.resend_id}`}
                    onClick={() => viewEmail(it.resend_id)}
                    className="border border-[#C4A671]/50 text-[#C4A671] px-4 py-2 text-[10px]
                               tracking-[0.18em] uppercase hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                  >
                    {openId === it.resend_id ? "Chiudi" : "Vedi email"}
                  </button>
                </div>
              </div>
              {openId === it.resend_id && (
                <div className="border-t border-[#1f1f1f] p-5 bg-[#FDFBF7] text-[#1a1a1a]">
                  {loadingContent ? (
                    <div className="text-sm text-[#7A7568]">Recupero il testo…</div>
                  ) : content?.html ? (
                    <div className="text-[14px] leading-relaxed max-h-[480px] overflow-y-auto"
                         dangerouslySetInnerHTML={{ __html: content.html }} />
                  ) : (
                    <pre className="text-[13px] whitespace-pre-wrap font-serif-display">{content?.text || "—"}</pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════ 2. RICERCA ══════════════════
function SearchPanel({ authHeader, onChanged }) {
  const [region, setRegion] = useState("Sicilia");
  const [cities, setCities] = useState([]);
  const [manualCity, setManualCity] = useState("");
  const [cats, setCats] = useState(["Ristorante gourmet"]);
  const [busy, setBusy] = useState(false);

  const toggleCity = (c) =>
    setCities(cities.includes(c) ? cities.filter((x) => x !== c) : [...cities, c]);
  const toggleCat = (p) =>
    setCats(cats.includes(p) ? cats.filter((x) => x !== p) : [...cats, p]);

  const addManual = () => {
    const c = manualCity.trim();
    if (c && !cities.includes(c)) setCities([...cities, c]);
    setManualCity("");
  };

  const keywords = cats.flatMap((prefix) => cities.map((city) => `${prefix} ${city}`));

  const search = async () => {
    if (!keywords.length) return toast.error("Seleziona almeno una città e una categoria");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/outreach/discover`,
        { keywords: keywords.slice(0, 30), max_per_keyword: 20 },
        authHeader
      );
      toast.success(`+${data.inserted} nuovi prospect · ${data.skipped_duplicates} duplicati · ricerca email automatica avviata in background`);
      if (data.failed_keywords?.length) toast.error(`Keyword fallite: ${data.failed_keywords.length}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore ricerca");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="font-serif-display italic text-2xl mb-1">Radar globale</h2>
      <p className="text-[13px] text-[#8B7A55] mb-8">
        Scegli territorio (Italia o mondo) e categorie: il radar interroga Google Places e cerca automaticamente le email sui siti dei locali trovati.
      </p>

      <div className="overline text-[9.5px] text-[#C4A671] mb-2">1 · Regione</div>
      <select
        data-testid="region-select"
        value={region}
        onChange={(e) => setRegion(e.target.value)}
        className="bg-[#111] border border-[#2a2a2a] px-4 py-3 text-[14px] w-64
                   focus:border-[#C4A671] outline-none mb-6"
      >
        {Object.keys(REGIONS).map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>

      <div className="overline text-[9.5px] text-[#C4A671] mb-2">2 · Città</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {REGIONS[region].map((c) => (
          <button
            key={c}
            data-testid={`city-${c.replace(/\s+/g, "-")}`}
            onClick={() => toggleCity(c)}
            className={`px-3.5 py-1.5 text-[12px] border transition ${
              cities.includes(c)
                ? "border-[#C4A671] bg-[#C4A671] text-[#0A0A0A]"
                : "border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60"
            }`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-6">
        <input
          data-testid="manual-city"
          value={manualCity}
          onChange={(e) => setManualCity(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addManual()}
          placeholder="Inserisci città manualmente…"
          className="bg-transparent border-b border-[#2a2a2a] py-2 px-1 text-[14px] w-64
                     focus:border-[#C4A671] outline-none placeholder:text-[#7A7568]"
        />
        <button
          data-testid="add-manual-city"
          onClick={addManual}
          className="text-[#C4A671] text-[12px] uppercase tracking-[0.18em] hover:text-white transition"
        >
          + Aggiungi
        </button>
      </div>
      {cities.filter((c) => !REGIONS[region].includes(c)).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 -mt-2">
          {cities.filter((c) => !REGIONS[region].includes(c)).map((c) => (
            <button
              key={c}
              onClick={() => toggleCity(c)}
              className="px-3.5 py-1.5 text-[12px] border border-[#C4A671] bg-[#C4A671] text-[#0A0A0A]"
            >
              {c} ✕
            </button>
          ))}
        </div>
      )}

      <div className="overline text-[9.5px] text-[#C4A671] mb-2">3 · Categorie target</div>
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.prefix}
            data-testid={`cat-${cat.label.replace(/\s+/g, "-")}`}
            onClick={() => toggleCat(cat.prefix)}
            className={`px-4 py-2 text-[12px] uppercase tracking-[0.14em] border transition ${
              cats.includes(cat.prefix)
                ? "border-[#C4A671] bg-[#C4A671] text-[#0A0A0A]"
                : "border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="border-t border-[#1f1f1f] pt-6 flex items-center justify-between flex-wrap gap-4">
        <div className="text-[12.5px] text-[#7A7568]">
          {keywords.length
            ? <>{keywords.length} ricerche pronte · es. <i className="text-[#C9C3B5]">"{keywords[0]}"</i></>
            : "Seleziona città e categorie"}
        </div>
        <button
          data-testid="run-search"
          onClick={search}
          disabled={busy || !keywords.length}
          className="bg-[#C4A671] text-[#0A0A0A] px-7 py-3.5 text-[10.5px] tracking-[0.24em]
                     uppercase font-medium hover:bg-white transition disabled:opacity-40"
        >
          {busy ? "Ricerca in corso…" : "◎ Cerca su Google Places"}
        </button>
      </div>

      <AutopilotBlock authHeader={authHeader} onChanged={onChanged} />
    </div>
  );
}

function AutopilotBlock({ authHeader, onChanged }) {
  const [run, setRun] = useState(null);
  const [limit, setLimit] = useState(30);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const loadSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/autopilot/settings`, authHeader);
      setSettings(data);
    } catch { /* silent */ }
  };
  const saveSettings = async (patch) => {
    try {
      const { data } = await axios.post(`${API}/admin/outreach/autopilot/settings`, patch, authHeader);
      setSettings(data);
      toast.success("Impostazioni Autopilota salvate.");
    } catch { toast.error("Errore salvataggio impostazioni."); }
  };
  useEffect(() => { loadSettings(); }, []); // eslint-disable-line

  const poll = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/autopilot/status`, authHeader);
      setRun(data);
      if (data?.status === "running") {
        wasRunning.current = true;
        timer.current = setTimeout(poll, 3500);
      } else if (wasRunning.current) {
        wasRunning.current = false;
        toast.success(`Autopilota terminato: ${data.ok} bozze in Approvazione · ${data.skipped} senza email · ${data.failed} errori.`);
        onChanged();
      }
    } catch { /* silent */ }
  };
  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line

  const start = async () => {
    if (!window.confirm(
      `Avviare l'Autopilota su massimo ${limit} prospect in stato Scoperto/Arricchito?\n\n` +
      "Catena per ciascuno: Analisi Profonda del sito (menu, carta vini, posizionamento) → Qualifica AI → " +
      "Bozza iper-personalizzata nella lingua del Paese → coda Approvazione.\n\nNESSUN invio automatico: l'ultimo click resta tuo."
    )) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/autopilot/start`, { limit: Number(limit) || 30 }, authHeader);
      toast.success(`Autopilota avviato su ${data.total} prospect.`);
      wasRunning.current = true;
      clearTimeout(timer.current);
      timer.current = setTimeout(poll, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio Autopilota.");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    try {
      await axios.post(`${API}/admin/outreach/autopilot/stop`, {}, authHeader);
      toast.info("Arresto richiesto: l'Autopilota si fermerà al termine del prospect corrente.");
    } catch { toast.error("Nessun Autopilota in corso."); }
  };

  const forceReset = async () => {
    if (!window.confirm("⛔ RESET DI EMERGENZA: chiude qualsiasi ciclo Autopilota bloccato e libera la coda. Procedere?")) return;
    try {
      const { data } = await axios.post(`${API}/admin/outreach/autopilot/force-reset`, {}, authHeader);
      toast.success(data.reset ? `Coda sbloccata: ${data.reset} run chiusi.` : "Nessun run bloccato: coda già libera.");
      onChanged && onChanged();
    } catch { toast.error("Reset non riuscito."); }
  };

  const sendReport = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/outreach/autopilot/report/send`, {}, authHeader);
      toast.success(`Rapporto inviato a ${data.sent_to}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio del rapporto non riuscito.");
    }
  };

  const running = run?.status === "running";
  const pct = run?.total ? Math.round((run.done / run.total) * 100) : 0;

  return (
    <div className="mt-12 border border-[#C4A671]/30 bg-[#0F0E0B] p-6" data-testid="autopilot-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-serif-display italic text-xl text-[#F2EFE8]">🤖 Autopilota Outreach</div>
          <p className="text-[12px] text-[#7A7568] mt-1 max-w-lg">
            Processa in batch i prospect Scoperti/Arricchiti: Analisi Profonda del sito → Qualifica →
            bozza iper-personalizzata (IT/EN/DE/FR automatico) → coda Approvazione. Zero invii automatici.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[11px] text-[#7A7568]">
            Max
            <input
              data-testid="autopilot-limit"
              type="number" min={1} max={100} value={limit}
              onChange={(e) => setLimit(e.target.value)}
              disabled={running}
              className="ml-2 w-16 bg-[#111] border border-[#2a2a2a] px-2 py-1.5 text-[13px] text-[#F2EFE8]
                         focus:border-[#C4A671] outline-none"
            />
          </label>
          {running ? (
            <>
              <button
                data-testid="autopilot-stop"
                onClick={stop}
                className="border border-[#c0392b]/60 text-[#e07b6d] px-5 py-2.5 text-[10px] tracking-[0.2em]
                           uppercase hover:bg-[#c0392b] hover:text-white transition"
              >
                ■ Arresta
              </button>
              <button
                data-testid="autopilot-force-reset"
                onClick={forceReset}
                title="Se l'Autopilota è congelato su un prospect, chiude il run e libera la coda"
                className="border border-[#c0392b] bg-[#c0392b]/15 text-[#e07b6d] px-4 py-2.5 text-[10px]
                           tracking-[0.2em] uppercase hover:bg-[#c0392b] hover:text-white transition"
              >
                ⛔ Reset emergenza
              </button>
            </>
          ) : (
            <button
              data-testid="autopilot-start"
              onClick={start}
              disabled={busy}
              className="bg-[#C4A671] text-[#0A0A0A] px-6 py-2.5 text-[10px] tracking-[0.22em]
                         uppercase font-medium hover:bg-white transition disabled:opacity-40"
            >
              {busy ? "Avvio…" : "▶ Avvia Autopilota"}
            </button>
          )}
        </div>
      </div>

      {settings && (
        <div className="mt-5 pt-4 border-t border-[#1f1f1f] flex flex-wrap items-center gap-x-6 gap-y-3"
             data-testid="autopilot-settings">
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="autopilot-scheduled-toggle">
            <input
              type="checkbox"
              checked={!!settings.scheduled_enabled}
              onChange={(e) => saveSettings({ scheduled_enabled: e.target.checked })}
              className="accent-[#C4A671] w-4 h-4"
            />
            <span className="text-[11.5px] text-[#C9C3B5]">🌅 Ciclo mattutino automatico <span className="text-[#7A7568]">(ore 06:00 UTC ≈ 07-08 in Italia)</span></span>
          </label>
          <label className="text-[11px] text-[#7A7568]">
            Soglia affinità
            <input
              data-testid="autopilot-min-affinity"
              type="number" min={0} max={100} defaultValue={settings.min_affinity}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== settings.min_affinity) saveSettings({ min_affinity: v }); }}
              className="ml-2 w-16 bg-[#111] border border-[#2a2a2a] px-2 py-1 text-[12.5px] text-[#C4A671] focus:border-[#C4A671] outline-none"
            />
            <span className="ml-1">/100 (sotto → scartato)</span>
          </label>
          <label className="text-[11px] text-[#7A7568]">
            Max mattutino
            <input
              data-testid="autopilot-daily-limit"
              type="number" min={1} max={50} defaultValue={settings.daily_limit}
              onBlur={(e) => { const v = Number(e.target.value); if (v !== settings.daily_limit) saveSettings({ daily_limit: v }); }}
              className="ml-2 w-14 bg-[#111] border border-[#2a2a2a] px-2 py-1 text-[12.5px] text-[#F2EFE8] focus:border-[#C4A671] outline-none"
            />
          </label>
          <button
            data-testid="autopilot-send-report"
            onClick={sendReport}
            className="text-[10px] uppercase tracking-[0.18em] border border-[#2a2a2a] px-3 py-1.5 text-[#7A7568]
                       hover:text-[#C4A671] hover:border-[#C4A671]/50 transition"
          >
            ✉ Invia rapporto ultimo ciclo
          </button>
        </div>
      )}

      {run && run.status !== "idle" && (
        <div className="mt-5" data-testid="autopilot-status">
          <div className="flex items-center justify-between text-[11px] text-[#8B7A55] mb-1.5">
            <span>
              {running
                ? <>In lavorazione: <i className="text-[#C9C3B5]">{run.current || "…"}</i></>
                : `Ultimo ciclo ${run.status === "stopped" ? "arrestato" : "completato"}`}
            </span>
            <span>{run.done}/{run.total} · ✓{run.ok} · ∅{run.skipped} · ✕{run.failed}</span>
          </div>
          <div className="h-[3px] w-full bg-[#1c1c1c]">
            <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: "#C4A671" }} />
          </div>
          {run.log?.length > 0 && (
            <div className="mt-3 flex flex-col gap-1 max-h-44 overflow-y-auto" data-testid="autopilot-log">
              {[...run.log].reverse().slice(0, 12).map((l, i) => (
                <div key={i} className="text-[11.5px] text-[#7A7568]">
                  <span className="text-[#C9C3B5]">{l.name}</span>
                  {l.city ? ` (${l.city})` : ""} — <span className={l.esito?.startsWith("errore") ? "text-[#e07b6d]" : "text-[#8B7A55]"}>{l.esito}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════ 3. ARCHIVIO ══════════════════
function GlobalProgressBar({ authHeader, onIdle }) {
  const [ops, setOps] = useState([]);
  const hadOps = useRef(false);
  useEffect(() => {
    let alive = true;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/outreach/progress`, authHeader);
        if (!alive) return;
        const next = data.ops || [];
        if (hadOps.current && next.length === 0 && onIdle) onIdle();
        hadOps.current = next.length > 0;
        setOps(next);
        timer = setTimeout(poll, next.length ? 4000 : 10000);
      } catch {
        if (alive) timer = setTimeout(poll, 10000);
      }
    };
    poll();
    return () => { alive = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!ops.length) return null;
  const eta = (op) => {
    if (!op.started_at || !op.done || !op.total) return null;
    const elapsed = (Date.now() - new Date(op.started_at).getTime()) / 1000;
    if (elapsed <= 0) return null;
    const remain = Math.max(0, ((op.total - op.done) * elapsed) / op.done);
    return remain < 60 ? `≈ ${Math.ceil(remain)}s` : `≈ ${Math.ceil(remain / 60)} min`;
  };
  return (
    <div className="border-b border-[#C4A671]/25 bg-[#0F0E0B] px-8 py-2.5 space-y-2" data-testid="global-progress">
      {ops.map((op) => {
        const pct = op.total ? Math.min(100, Math.round((op.done / op.total) * 100)) : 0;
        const e = eta(op);
        return (
          <div key={op.kind} data-testid={`progress-${op.kind}`} className="flex items-center gap-4">
            <span className="text-[11px] text-[#C4A671] shrink-0 w-44 truncate">{op.label}</span>
            <div className="flex-1 h-1.5 bg-[#1f1f1f] overflow-hidden">
              <div className="h-full bg-[#C4A671] transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[11px] text-[#C9C3B5] shrink-0" data-testid={`progress-${op.kind}-count`}>
              {op.total ? `${op.done} di ${op.total} · ${pct}%` : `${op.done} completati…`}
            </span>
            {e && <span className="text-[10.5px] text-[#7A7568] shrink-0">{e} rimanenti</span>}
            {op.current && (
              <span className="text-[10.5px] text-[#7A7568] italic truncate max-w-[240px]">«{op.current}»</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ArchivePanel({
  prospects, filter, setFilter, selected, setSelected, authHeader,
  busy, runPipeline, generateDraft, sendEmail, discardSelected, deleteSelected, markReplied, setStatus, setManualEmail, onChanged,
}) {
  const [checkedIds, setCheckedIds] = useState({});
  const [genBusy, setGenBusy] = useState(false);
  const numChecked = Object.values(checkedIds).filter(Boolean).length;

  const generateSelected = async () => {
    const ids = Object.keys(checkedIds).filter((id) => checkedIds[id]);
    if (!ids.length) return toast.error("Seleziona almeno un contatto");
    if (!window.confirm(`Generare le prime email per ${ids.length} contatti selezionati?\n\nLe bozze arriveranno nel tab "Approvazione Veloce" (nessun invio automatico).`)) return;
    setGenBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/daily-batch/start-selected`, { ids }, authHeader);
      toast.success(`${data.selected} contatti in generazione — vai in "Approvazione Veloce" tra poco ✨`);
      setCheckedIds({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio generazione");
    }
    setGenBusy(false);
  };

  const pick = async (p) => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/prospects/${p.id}`, authHeader);
      setSelected(data);
    } catch { /* noop */ }
  };

  const ARCHIVE_TABS = [
    ["nuovi", "🆕 Nuovi", ["discovered"]],
    ["arricchiti", "📖 Arricchiti", ["enriched"]],
    ["qualificati", "🎯 Qualificati", ["qualified"]],
    ["coda", "⏳ In Coda", ["queued"]],
    ["inviate", "✉ Inviate", ["sent", "opened", "clicked"]],
    ["trattative", "💬 Trattative", ["replied"]],
    ["concluse", "🗄 Concluse", ["skipped", "opted_out", "bounced", "won"]],
  ];
  const SPLIT_TABS = ["nuovi", "arricchiti", "qualificati", "coda"];
  const [atab, setAtab] = useState("nuovi");
  const tabDef = ARCHIVE_TABS.find(([k]) => k === atab);
  let tabItemsAll = prospects.filter((p) => tabDef[2].includes(p.status));
  if (atab === "nuovi") {
    tabItemsAll = [...tabItemsAll].sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1));
  }
  const isSplit = SPLIT_TABS.includes(atab);
  const withoutEmail = isSplit ? tabItemsAll.filter((p) => !p.primary_email) : [];
  const tabItems = isSplit ? tabItemsAll.filter((p) => p.primary_email) : tabItemsAll;
  const countFor = (statuses) => prospects.filter((p) => statuses.includes(p.status)).length;

  const idsChecked = () => Object.keys(checkedIds).filter((id) => checkedIds[id]);
  const bulkStatus = async (status, label) => {
    const ids = idsChecked();
    if (!ids.length) return toast.error("Seleziona almeno un contatto");
    if (!window.confirm(`${label} per ${ids.length} contatti selezionati?`)) return;
    try {
      await axios.post(`${API}/admin/outreach/prospects/bulk-status`, { ids, status }, authHeader);
      toast.success(`${ids.length} contatti → ${STATUS_LABEL[status]}`);
      setCheckedIds({});
      onChanged && onChanged();
    } catch { toast.error("Azione di massa non riuscita"); }
  };
  const bulkDelete = async () => {
    const ids = idsChecked();
    if (!ids.length) return toast.error("Seleziona almeno un contatto");
    if (!window.confirm(`⚠️ ELIMINARE DEFINITIVAMENTE ${ids.length} contatti?\n\nQuesta azione non è reversibile.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/outreach/prospects/bulk-delete`, { ids }, authHeader);
      toast.success(`${data.deleted} contatti eliminati definitivamente`);
      setCheckedIds({});
      onChanged && onChanged();
    } catch { toast.error("Eliminazione non riuscita"); }
  };

  const huntNoEmail = async () => {
    const ids = withoutEmail.map((p) => p.id).slice(0, 50);
    if (!ids.length) return;
    if (!window.confirm(`🔍 Avviare la caccia su ${ids.length} contatti senza email?\n\nPer ognuno cerco il sito su Google e l'email sul sito: chi ce l'ha arriva in Approvazione Veloce con la bozza pronta, chi resta davvero senza va in Ricerca Manuale.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/outreach/daily-batch/start-selected`, { ids }, authHeader);
      toast.success(`Caccia avviata su ${data.selected} contatti — segui la barra di avanzamento in alto ⏳`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Avvio non riuscito"); }
  };

  const GEN_ACTION = { label: `⚡ Genera email (${numChecked})`, run: generateSelected, primary: true };
  const SCARTA_ACTION = { label: "✕ Scarta", run: () => bulkStatus("skipped", "Scartare") };
  const MASS_ACTIONS = {
    nuovi: [GEN_ACTION, SCARTA_ACTION],
    arricchiti: [GEN_ACTION, SCARTA_ACTION],
    qualificati: [GEN_ACTION, SCARTA_ACTION],
    coda: [SCARTA_ACTION],
    inviate: [
      { label: "✓ Segna risposti", run: () => bulkStatus("replied", "Segnare come Risposto"), primary: true },
      { label: "✕ Scarta", run: () => bulkStatus("skipped", "Scartare") },
    ],
    trattative: [
      { label: "🏆 Cliente acquisito", run: () => bulkStatus("won", "Segnare come Cliente acquisito 🏆"), primary: true },
      { label: "✕ Scarta", run: () => bulkStatus("skipped", "Scartare") },
    ],
    concluse: [
      { label: "↩ Ripristina in coda", run: () => bulkStatus("enriched", "Ripristinare in coda"), primary: true },
      { label: "🗑 Elimina definitivamente", run: bulkDelete, danger: true },
    ],
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_1fr] gap-6">
      {/* Filtri */}
      <aside className="space-y-5">
        <div className="overline text-[10px] text-[#C4A671]">Filtri</div>
        <div>
          <label className="overline text-[9px] block mb-1">Min fit score</label>
          <input
            data-testid="filter-score"
            type="number"
            value={filter.min_score}
            onChange={(e) => setFilter({ ...filter, min_score: e.target.value })}
            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 text-[13px] focus:border-[#C4A671] outline-none"
          />
        </div>
        <div>
          <label className="overline text-[9px] block mb-1">Città</label>
          <input
            data-testid="filter-city"
            value={filter.city}
            onChange={(e) => setFilter({ ...filter, city: e.target.value })}
            placeholder="es. Tokyo, Capri…"
            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 text-[13px] focus:border-[#C4A671] outline-none"
          />
        </div>
        <div>
          <label className="overline text-[9px] block mb-1">Paese (codice)</label>
          <input
            data-testid="filter-country"
            value={filter.country}
            onChange={(e) => setFilter({ ...filter, country: e.target.value.toUpperCase() })}
            placeholder="es. IT, JP, MC, FR…"
            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 text-[13px] focus:border-[#C4A671] outline-none uppercase"
          />
        </div>
      </aside>

      {/* Lista */}
      <section>
        <div className="flex gap-1.5 flex-wrap mb-3" data-testid="archive-tabs">
          {ARCHIVE_TABS.map(([key, label, statuses]) => (
            <button
              key={key}
              data-testid={`archive-tab-${key}`}
              onClick={() => { setAtab(key); setCheckedIds({}); }}
              className={`px-3.5 py-2 text-[9.5px] uppercase tracking-[0.16em] border transition ${
                atab === key ? "border-[#C4A671] text-[#C4A671]" : "border-[#2a2a2a] text-[#7A7568] hover:text-[#C9C3B5]"
              }`}
            >
              {label} ({countFor(statuses)})
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap mb-3 border border-[#C4A671]/25 bg-[#C4A671]/5 px-4 py-3">
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#C9C3B5] cursor-pointer">
            <input
              type="checkbox"
              data-testid="archive-check-all"
              checked={tabItems.length > 0 && tabItems.every((p) => checkedIds[p.id])}
              onChange={(e) => {
                const next = { ...checkedIds };
                tabItems.forEach((p) => { next[p.id] = e.target.checked; });
                setCheckedIds(next);
              }}
              className="accent-[#C4A671] w-4 h-4"
            />
            Seleziona tutti ({tabItems.length})
          </label>
          <div className="flex gap-2 flex-wrap">
            {MASS_ACTIONS[atab].map((a, i) => (
              <button
                key={i}
                data-testid={`archive-mass-${atab}-${i}`}
                onClick={a.run}
                disabled={genBusy || !numChecked}
                className={`px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-medium transition disabled:opacity-40 ${
                  a.primary
                    ? "bg-[#C4A671] text-[#0A0A0A] hover:bg-white"
                    : a.danger
                      ? "border border-[#c0392b]/60 text-[#e07b6d] hover:bg-[#c0392b] hover:text-white"
                      : "border border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto pr-2">
          {atab === "nuovi" && tabItemsAll.length > 0 && (
            <div className="mb-4 border border-[#1f1f1f] px-4 py-3 text-[12px] text-[#7A7568]" data-testid="nuovi-note">
              🆕 Contatti appena scoperti dalla Ricerca o dalla Sentinella, mai ancora lavorati. Quelli con email:
              selezionali e premi <b className="text-[#C4A671]">⚡ Genera email</b> → bozze in Approvazione Veloce.
              Quelli senza email: premi <b className="text-[#C4A671]">🔍 Caccia email</b> nel gruppo qui sotto —
              cerco sito ed email su Google prima di mandarli in Ricerca Manuale.
            </div>
          )}
          {atab === "coda" && tabItems.length > 0 && (
            <div className="mb-4 border border-[#1f1f1f] px-4 py-3 text-[12px] text-[#7A7568]" data-testid="coda-note">
              ⏳ Questi contatti hanno già la bozza pronta: la trovi in{" "}
              <b className="text-[#C4A671]">✓ Approvazione Veloce</b>, da lì approvi e invii.
            </div>
          )}
          {tabItemsAll.length === 0 && (
            <div className="text-[#7A7568] text-sm py-8 text-center">
              {atab === "nuovi"
                ? "Nessun contatto nuovo — usa ◎ Ricerca o la 🎯 Sentinella per scoprirne altri."
                : "Nessun contatto in questa vista."}
            </div>
          )}
          {atab === "inviate" ? (
            <>
              {[
                ["clicked", "🔥 Cliccati — Hot Lead", "#E0B45A", "Sono andati sul sito: nessun follow-up automatico, li lavori tu personalmente."],
                ["opened", "👀 Aperti — Warm Lead", "#9FBF8F", "Hanno aperto senza cliccare: ricevono il sollecito sintetico Anteprima 2025 a 8 giorni."],
                ["sent", "❄️ In attesa — Cold Lead", "#7A8FA6", "Mai aperte: rilancio automatico a 15 giorni con il nuovo prompt Outreach (angolo e oggetto diversi)."],
              ].map(([st, title, color, note]) => {
                const items = tabItems.filter((p) => p.status === st);
                if (!items.length) return null;
                return (
                  <div key={st}>
                    <ProspectGroup
                      title={`${title} (${items.length})`}
                      color={color}
                      items={items}
                      selected={selected}
                      onPick={pick}
                      testId={`group-inviate-${st}`}
                      checkedIds={checkedIds}
                      onToggleCheck={(id) => setCheckedIds((prev) => ({ ...prev, [id]: !prev[id] }))}
                    />
                    <div className="mb-6 -mt-2 px-1 text-[11px] text-[#5a5548]" data-testid={`inviate-note-${st}`}>
                      {note}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (tabItems.length > 0 || !isSplit) && (
            <ProspectGroup
              title={isSplit ? `✉️ Con email (${tabItems.length})` : `${tabDef[1]} (${tabItems.length})`}
              color="#C4A671"
              items={tabItems}
              selected={selected}
              onPick={pick}
              testId={`group-${atab}`}
              checkedIds={checkedIds}
              onToggleCheck={(id) => setCheckedIds((prev) => ({ ...prev, [id]: !prev[id] }))}
            />
          )}
          {isSplit && withoutEmail.length > 0 && (
            <>
              <ProspectGroup
                title={`📭 Senza email (${withoutEmail.length})`}
                color="#7A7568"
                items={withoutEmail}
                selected={selected}
                onPick={pick}
                testId={`group-${atab}-noemail`}
                headerAction={atab === "nuovi" ? (
                  <span
                    data-testid="nuovi-hunt-noemail"
                    onClick={(e) => { e.stopPropagation(); huntNoEmail(); }}
                    className="text-[9.5px] uppercase tracking-[0.16em] border border-[#C4A671]/50 text-[#C4A671] px-3 py-1.5 cursor-pointer hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                  >
                    🔍 Caccia email (tutti)
                  </span>
                ) : null}
              />
              {atab !== "nuovi" && (
                <div className="mb-5 border border-[#1f1f1f] px-4 py-3 text-[12px] text-[#7A7568]" data-testid="noemail-note">
                  📭 Questi contatti sono in attesa di email: li gestisci nella scheda{" "}
                  <b className="text-[#C4A671]">🔎 Ricerca Manuale</b> — appena inserisci l'email, la bozza si genera
                  da sola e la scheda passa in Approvazione Veloce.
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Dettaglio */}
      <section>
        {!selected ? (
          <div className="text-[#7A7568] text-sm pt-16 text-center">
            Selezioni un prospect per gestirlo.
          </div>
        ) : (
          <ProspectDetail
            p={selected}
            busy={busy}
            onEnrich={() => runPipeline("enrich")}
            onQualify={() => runPipeline("qualify")}
            onGenerate={generateDraft}
            onDry={(attach) => sendEmail(true, attach)}
            onSend={(attach) => {
              if (window.confirm(`INVIO REALE a ${selected.primary_email}?${attach ? "\n\n📎 Con allegato: Schede Tecniche Annata 2025 (PDF)" : ""}`)) sendEmail(false, attach);
            }}
            onDiscard={discardSelected}
            onDelete={deleteSelected}
            onReplied={markReplied}
            onSetStatus={setStatus}
            onSetEmail={setManualEmail}
            onAllocationCode={async () => {
              if (!window.confirm(`Generare e inviare un Codice di Allocazione a ${selected.primary_email}?\n\nLo chef riceverà un'email elegante con codice e link diretto al Registro.`)) return;
              try {
                const { data } = await axios.post(`${API}/admin/registry/codes/from-prospect/${selected.id}`, {}, authHeader);
                toast.success(
                  data.reused
                    ? `Codice esistente ${data.code} re-inviato a ${data.email}.`
                    : `Codice ${data.code} generato e inviato a ${data.email}.`
                );
                setSelected({ ...selected, allocation_code: data.code });
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Errore generazione codice.");
              }
            }}
            onDeepAnalyze={async () => {
              toast.info(`Analisi Profonda di ${selected.business_name} in corso… (30-60s)`);
              try {
                const { data } = await axios.post(`${API}/admin/outreach/deep-analyze/${selected.id}`, {}, authHeader);
                setSelected({ ...selected, dossier: data.dossier });
                toast.success(`Dossier pronto — affinità ${data.dossier.affinita}/100.`);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Analisi non riuscita.");
              }
            }}
          />
        )}
      </section>
    </div>
  );
}

function ProspectGroup({ title, subtitle, color, items, selected, onPick, testId, collapsible, startCollapsed, headerAction, checkedIds, onToggleCheck }) {
  const [collapsed, setCollapsed] = useState(!!startCollapsed);
  return (
    <div className="mb-5" data-testid={testId}>
      <button
        onClick={() => collapsible && setCollapsed(!collapsed)}
        className={`w-full text-left flex items-center justify-between py-2 border-b ${collapsible ? "cursor-pointer" : "cursor-default"}`}
        style={{ borderColor: `${color}55` }}
      >
        <div>
          <span className="overline text-[10px]" style={{ color }}>{title}</span>
          {subtitle && !collapsed && (
            <span className="block text-[10.5px] text-[#7A7568] mt-0.5 normal-case tracking-normal">{subtitle}</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {headerAction}
          {collapsible && <span className="text-[11px]" style={{ color }}>{collapsed ? "▸ mostra" : "▾ nascondi"}</span>}
        </div>
      </button>
      {!collapsed && (
        items.length === 0 ? (
          <div className="text-[#5a564c] text-[12px] py-3 px-1">Nessun prospect in questa sezione.</div>
        ) : (
          items.map((p) => (
            <div key={p.id} className="flex items-stretch border-b border-[#161616]">
              {onToggleCheck && (
                <label className="flex items-center px-2 cursor-pointer hover:bg-[#111]">
                  <input
                    type="checkbox"
                    data-testid={`archive-check-${p.id}`}
                    checked={!!checkedIds?.[p.id]}
                    onChange={() => onToggleCheck(p.id)}
                    className="accent-[#C4A671] w-4 h-4"
                  />
                </label>
              )}
              <button
                data-testid={`prospect-row-${p.id}`}
                onClick={() => onPick(p)}
                className={`flex-1 text-left px-4 py-3 hover:bg-[#111]
                            transition flex items-center justify-between gap-3 ${
                              selected?.id === p.id ? "bg-[#111]" : ""
                            }`}
              >
              <div className="min-w-0">
                <div className="font-serif-display text-[15px] truncate">
                  {p.business_name}
                  {p.keyword === "concierge-b2b" && (
                    <span className="ml-2 overline text-[8px] px-1.5 py-0.5 border border-[#7CB87C]/60 text-[#7CB87C]" data-testid={`badge-concierge-${p.id}`}>
                      💬 Concierge B2B
                    </span>
                  )}
                </div>
                <div className="overline text-[8.5px] text-[#7A7568] mt-0.5">
                  {p.city || "—"}{p.country && p.country !== "IT" ? ` (${p.country})` : ""}{p.primary_email ? ` · ${p.primary_email}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                {p.fit_score != null && (
                  <div className="text-[13px] text-[#C4A671]">{p.fit_score}<span className="text-[8px] text-[#7A7568] ml-1">AI</span></div>
                )}
                {p.rating != null && (
                  <div className="text-[11px] text-[#C9C3B5]">★ {p.rating}<span className="text-[8px] text-[#7A7568] ml-1">Google</span></div>
                )}
                {p.fit_score == null && p.rating == null && <div className="text-[13px] text-[#7A7568]">—</div>}
                <div className="overline text-[8px] text-[#7A7568]">{STATUS_LABEL[p.status]}</div>
              </div>
            </button>
            </div>
          ))
        )
      )}
    </div>
  );
}

function ProspectDetail({ p, busy, onEnrich, onQualify, onGenerate, onDry, onSend, onDiscard, onDelete, onReplied, onSetStatus, onSetEmail, onAllocationCode, onDeepAnalyze }) {
  const [manualEmail, setManualEmail] = useState("");
  const [attachScheda, setAttachScheda] = useState(false);
  return (
    <div className="space-y-4" data-testid="prospect-detail">
      <div>
        <h2 className="font-serif-display italic text-2xl">{p.business_name}</h2>
        {p.keyword === "concierge-b2b" && (
          <div className="mt-2 inline-flex items-center gap-1.5 border border-[#7CB87C]/60 text-[#7CB87C] px-2.5 py-1 overline text-[9px]" data-testid="detail-badge-concierge">
            💬 Richiesta B2B dal Concierge — contatto inbound caldo
          </div>
        )}
        <div className="text-[13px] text-[#8B7A55] mt-1">
          {p.city} · {p.formatted_address}
        </div>
      </div>

      <Field label="Website">{p.website || "—"}</Field>
      <Field label="Email primaria">
        {p.primary_email || <span className="text-[#E06C5A]">Nessuna email trovata</span>}
      </Field>
      {!p.primary_email && (
        <div className="border border-[#E06C5A]/40 bg-[#1a0f0d] p-3.5" data-testid="no-email-box">
          <div className="text-[12px] text-[#E06C5A] mb-2">
            ⚠ Senza email non si può inviare. Prova "Enrich sito" oppure inseriscila a mano
            (la trovi su Google/Instagram del locale):
          </div>
          <div className="flex gap-2">
            <input
              data-testid="manual-email-input"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              placeholder="info@ristorante.it"
              className="flex-1 bg-transparent border-b border-[#7A7568] py-1.5 text-[13px]
                         focus:border-[#C4A671] outline-none placeholder:text-[#7A7568]"
            />
            <button
              data-testid="manual-email-save"
              onClick={() => manualEmail.trim() && onSetEmail(manualEmail.trim())}
              disabled={busy || !manualEmail.trim()}
              className="border border-[#C4A671]/60 text-[#C4A671] px-3 py-1.5 text-[10px]
                         tracking-[0.16em] uppercase hover:bg-[#C4A671] hover:text-[#0A0A0A]
                         transition disabled:opacity-40"
            >
              Salva email
            </button>
          </div>
        </div>
      )}
      <Field label="Telefono">{p.phone || "—"}</Field>
      <Field label="Fit score / Ragione">
        {p.fit_score != null ? `${p.fit_score}/100 — ${p.fit_reason || ""}` : "—"}
      </Field>
      <Field label="Status">{STATUS_LABEL[p.status]}</Field>
      {p.allocation_code && (
        <Field label="Codice Allocazione">
          <span className="tracking-[0.15em]" style={{ color: "#C4A671" }}>{p.allocation_code}</span>
        </Field>
      )}

      {p.dossier && (
        <div className="border border-[#2a2a2a] p-4 bg-[#0F0E0B]" data-testid="dossier-block">
          <div className="overline text-[9.5px] text-[#C4A671]">
            🔎 Dossier Analisi Profonda · affinità {p.dossier.affinita}/100 · {p.dossier.pages_analyzed} pagine analizzate
          </div>
          <div className="mt-2 text-[12.5px] leading-[1.75] text-[#C9C3B5] space-y-1">
            <p><span className="text-[#7A7568]">Posizionamento:</span> {p.dossier.posizionamento}</p>
            <p><span className="text-[#7A7568]">Cucina:</span> {p.dossier.stile_cucina} · fascia {p.dossier.fascia_prezzo}</p>
            <p><span className="text-[#7A7568]">Carta vini:</span> {p.dossier.carta_vini}</p>
            {p.dossier.vini_siciliani?.length > 0 && (
              <p><span className="text-[#7A7568]">Vini siciliani in carta:</span> {p.dossier.vini_siciliani.join(", ")}</p>
            )}
            {p.dossier.ganci?.length > 0 && (
              <ul className="list-disc pl-5 pt-1">
                {p.dossier.ganci.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {p.current_draft && (
        <div className="border border-[#C4A671]/40 p-4 bg-[#111]">
          <div className="overline text-[9.5px] text-[#C4A671]">Bozza attuale · {p.current_draft.variant}</div>
          <div className="mt-2 font-serif-display text-lg">{p.current_draft.subject}</div>
          <div className="mt-3 text-[13.5px] leading-[1.75] max-h-64 overflow-y-auto"
               dangerouslySetInnerHTML={{ __html: p.current_draft.html }} />
        </div>
      )}

      <label className="flex items-center gap-2 pt-2 cursor-pointer select-none text-[12px] text-[#C9C3B5]" data-testid="attach-scheda-toggle">
        <input
          type="checkbox"
          checked={attachScheda}
          onChange={(e) => setAttachScheda(e.target.checked)}
          className="accent-[#C4A671] w-4 h-4"
          data-testid="attach-scheda-checkbox"
        />
        📎 Allega <span className="text-[#C4A671]">Schede Tecniche Annata 2025</span> (PDF) all'invio
        <a href="/docs/Schede_Tecniche_Annata_2025_La_Valle_degli_Iblei.pdf" target="_blank" rel="noreferrer" className="text-[#7A7568] underline ml-1">anteprima ↗</a>
      </label>
      <div className="flex flex-wrap gap-2 pt-2">
        <Action onClick={onEnrich} disabled={busy}>Enrich sito</Action>
        <Action onClick={onQualify} disabled={busy}>Qualifica AI</Action>
        <Action onClick={() => onGenerate("initial")} disabled={busy} data-testid="generate-draft">Genera bozza</Action>
        <Action onClick={() => onGenerate("followup_1")} disabled={busy}>Bozza follow-up</Action>
        <Action onClick={() => onDry(attachScheda)} disabled={busy} tone="gold">Dry-run</Action>
        <Action onClick={() => onSend(attachScheda)} disabled={busy || !p.current_draft || !p.primary_email} tone="red" data-testid="send-real">
          Invia reale{attachScheda ? " + 📎" : ""}
        </Action>
        <Action onClick={onAllocationCode} disabled={busy || !p.primary_email} data-testid="gen-allocation-code">
          ✦ Genera &amp; Invia Codice
        </Action>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#1f1f1f] mt-3">
        <Action onClick={onReplied} disabled={busy} data-testid="mark-replied">✓ Segna Risposto</Action>
        <Action
          onClick={() => {
            if (window.confirm(`Segnare "${p.business_name}" come 🏆 CLIENTE ACQUISITO (allocazione chiusa)?`)) onSetStatus("won");
          }}
          disabled={busy}
          data-testid="mark-won"
        >
          🏆 Cliente acquisito
        </Action>
        <Action onClick={onDiscard} disabled={busy} data-testid="discard-prospect">✕ Scarta</Action>
        <Action onClick={onDelete} disabled={busy} tone="red" data-testid="delete-prospect">🗑 Elimina definitivamente</Action>
        <select
          data-testid="set-status-select"
          value=""
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value;
            if (v && window.confirm(`Correggere lo status di "${p.business_name}" in "${STATUS_LABEL[v]}"?`)) {
              onSetStatus(v);
            }
            e.target.value = "";
          }}
          className="bg-[#111] border border-[#2a2a2a] px-3 py-2 text-[11px] uppercase tracking-[0.14em]
                     text-[#C9C3B5] focus:border-[#C4A671] outline-none"
        >
          <option value="">⚙ Correggi status…</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {p.sent_emails?.length > 0 && (
        <div>
          <div className="overline text-[9.5px] text-[#C4A671] mt-4 mb-2">Cronologia invii</div>
          {p.sent_emails.map((s, i) => (
            <div key={i} className="text-[12.5px] text-[#C9C3B5] border-b border-[#1f1f1f] py-2">
              [{s.variant}] {s.subject} — {new Date(s.sent_at).toLocaleString("it-IT")}
              {s.opened_at && " · aperto"}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-[9px] text-[#8B7A55] mb-1">{label}</div>
      <div className="text-[14px] text-[#F2EFE8]">{children}</div>
    </div>
  );
}

function Action({ children, tone, ...rest }) {
  const styles = {
    default: "border border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A]",
    gold: "bg-[#C4A671] text-[#0A0A0A] hover:bg-white",
    red: "bg-[#63171B] text-white hover:bg-white hover:text-[#63171B]",
  };
  return (
    <button
      {...rest}
      className={`px-3.5 py-2 text-[10px] tracking-[0.18em] uppercase transition
                  disabled:opacity-40 ${styles[tone || "default"]}`}
    >
      {children}
    </button>
  );
}

function StatusChip({ label, value, good }) {
  return (
    <div className="text-center px-3">
      <span className="overline text-[8.5px] text-[#7A7568] block">{label}</span>
      <span className={`text-[15px] font-serif-display ${good ? "text-[#7CB87C]" : "text-[#C4A671]"}`}>
        {value}
      </span>
    </div>
  );
}


// ══════════════════ FOLLOW-UP AUTOMATICI ══════════════════
function FollowupPanel({ authHeader }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState([]);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/outreach/followups`, authHeader);
      setData(d);
    } catch {
      toast.error("Impossibile caricare i follow-up");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async () => {
    if (!data) return;
    const next = !data.enabled;
    if (next && !window.confirm(
      `⚠️ Stai per DISATTIVARE la Modalità Grilletto Manuale.\n\nIl sistema reinvierà DA SOLO i follow-up ai contatti scaduti (ciclo ogni 30 minuti, ${data.days} gg 👀 / 15 gg ❄️), rispettando il limite giornaliero.\n\nConfermi l'attivazione degli invii automatici?`
    )) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/outreach/config`, { followup_enabled: next }, authHeader);
      toast.success(next
        ? "Follow-up automatici ATTIVI — primo ciclo avviato subito, poi controllo ogni 30 minuti"
        : "Follow-up automatici disattivati");
      await load();
    } catch {
      toast.error("Errore salvataggio");
    }
    setBusy(false);
  };

  const changeDays = async () => {
    const v = window.prompt("Dopo quanti giorni senza risposta inviare il follow-up? (2–30)", data?.days ?? 7);
    if (v === null) return;
    const days = parseInt(v, 10);
    if (isNaN(days) || days < 2 || days > 30) { toast.error("Inserisci un numero tra 2 e 30"); return; }
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/outreach/config`, { followup_days: days }, authHeader);
      toast.success(`Follow-up dopo ${days} giorni`);
      await load();
    } catch {
      toast.error("Errore salvataggio");
    }
    setBusy(false);
  };

  const runNow = async () => {
    setBusy(true);
    let preview = null;
    try {
      const { data: pv } = await axios.get(`${API}/admin/outreach/followups/preview`, authHeader);
      preview = pv;
    } catch {
      toast.error("Impossibile caricare l'anteprima del lotto");
      setBusy(false);
      return;
    }
    if (!preview.total) {
      toast.info("Nessun contatto pronto per il follow-up in questo momento");
      setBusy(false);
      return;
    }
    const ok = window.confirm(
      `📋 ANTEPRIMA DEL LOTTO\n\n` +
      `Stai per inviare ${preview.will_send} follow-up (su ${preview.total} eleggibili):\n` +
      `  • 👀 Aperti (≥${preview.days} gg): ${preview.warm}\n` +
      `  • ❄️ In attesa (≥${preview.cold_days} gg): ${preview.cold}\n\n` +
      `Quota giornaliera: ${preview.sent_today}/${preview.cap} già inviate oggi.\n\n` +
      `Confermi l'avvio della sequenza? Le email partiranno REALMENTE.`
    );
    if (!ok) { setBusy(false); return; }
    try {
      const { data: res } = await axios.post(`${API}/admin/outreach/followups/run`, {}, authHeader);
      toast.success(res.sent > 0
        ? `${res.sent} follow-up inviati: ${(res.businesses || []).join(", ")}`
        : "Nessun contatto pronto per il follow-up in questo momento");
      await load();
    } catch {
      toast.error("Errore durante il ciclo follow-up");
    }
    setBusy(false);
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const isDue = (iso) => iso && new Date(iso) <= new Date();

  const toggleCheck = (id) =>
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const generateDrafts = async () => {
    const ids = checked.length ? checked : (data?.pending || []).filter((p) => !p.draft).map((p) => p.id);
    if (!ids.length) { toast.info("Tutte le bozze sono già pronte."); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/outreach/followups/generate-drafts`, { ids }, authHeader);
      toast.success(`Generazione di ${res.generating} bozze avviata in background ✨ — tra qualche istante le vedrai qui, pronte da rileggere e ritoccare.`);
      setChecked([]);
      let polls = 0;
      const timer = setInterval(async () => {
        polls += 1;
        await load();
        if (polls >= 6) clearInterval(timer);
      }, 10000);
    } catch {
      toast.error("Errore avvio generazione bozze");
    }
    setBusy(false);
  };

  if (!data) return <div className="text-[#7A7568] text-sm py-8">Caricamento…</div>;

  return (
    <div className="max-w-5xl" data-testid="followup-panel">
      <h2 className="font-serif-display italic text-2xl mb-1">Follow-up — Modalità Grilletto Manuale</h2>
      <p className="text-[13px] text-[#8B7A55] mb-5">
        <strong>🎯 On-Demand:</strong> nessun invio parte da solo. Le bozze restano in "Bozza Pronta / In Attesa"
        finché non premi tu <strong>🚀 Avvia Sequenza</strong> (con anteprima del lotto prima della conferma) o
        attivi consapevolmente il toggle Automatici. Regia: <strong>👀 Aperti</strong> a {data.days} gg sollecito
        sintetico "Pre-Allocazione 2025" (max 60 parole, senza link né Registro) → <strong>❄️ Mai aperti</strong> a
        15 gg rilancio col nuovo prompt. <strong>🔥 Cliccati: sempre esclusi.</strong> Se prepari o ritocchi una
        bozza qui sotto, invierà <em>la tua versione</em>.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <button
          data-testid="followup-toggle"
          onClick={toggle}
          disabled={busy}
          className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border transition disabled:opacity-40 ${
            data.enabled
              ? "border-[#7CB87C] bg-[#7CB87C]/10 text-[#7CB87C]"
              : "border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60"
          }`}
        >
          {data.enabled ? "● Automatici ATTIVI" : "○ Manuale (automatici spenti)"}
        </button>
        <button
          data-testid="followup-days"
          onClick={changeDays}
          disabled={busy}
          className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60 transition disabled:opacity-40"
        >
          Attesa: {data.days} giorni ✎
        </button>
        <button
          data-testid="followup-run-now"
          onClick={runNow}
          disabled={busy}
          className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#C4A671]/60 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
        >
          🚀 Avvia Sequenza
        </button>
      </div>

      {/* In programma — Stazione di Controllo */}
      <section className="mb-10">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="overline text-[10px] text-[#C4A671]">
            ⏳ In attesa di follow-up ({data.pending.length})
          </h3>
          {data.pending.length > 0 && (
            <button
              data-testid="followup-generate-drafts"
              onClick={generateDrafts}
              disabled={busy}
              className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] border border-[#C4A671]/60 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
            >
              ✨ Genera bozze {checked.length ? `(${checked.length} selezionati)` : "per tutti i senza-bozza"}
            </button>
          )}
        </div>
        {data.pending.length === 0 ? (
          <div className="border border-[#1f1f1f] p-8 text-center text-[#7A7568] text-sm">
            Nessun contatto in attesa: hanno tutti risposto o ricevuto già il follow-up.
          </div>
        ) : (
          <div className="space-y-1" data-testid="followup-pending-list">
            {data.pending.map((p) => (
              <FollowupCard
                key={p.id} p={p} authHeader={authHeader} fmtDate={fmtDate} isDue={isDue} onSent={load}
                checked={checked.includes(p.id)} onToggleCheck={() => toggleCheck(p.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Riinviate */}
      <section>
        <h3 className="overline text-[10px] text-[#C4A671] mb-3">
          ↻ Follow-up inviati ({data.sent.length})
        </h3>
        {data.sent.length === 0 ? (
          <div className="border border-[#1f1f1f] p-8 text-center text-[#7A7568] text-sm">
            Nessun follow-up inviato finora.
          </div>
        ) : (
          <div className="space-y-1" data-testid="followup-sent-list">
            {data.sent.map((s, i) => (
              <div key={`${s.id}-${i}`} className="border border-[#161616] bg-[#0d0d0d] px-5 py-3.5 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif-display text-[15px]">{s.business_name}</div>
                  <div className="text-[12px] text-[#7A7568] mt-0.5">
                    {s.city || "—"} · a: {s.to} · {s.sent_at ? new Date(s.sent_at).toLocaleString("it-IT") : "—"}
                  </div>
                  <div className="text-[13px] italic text-[#C9C3B5] mt-1">«{s.subject}»</div>
                </div>
                <span className="text-[11px] text-[#7A7568] shrink-0">
                  {s.status === "replied" ? "✓ ha risposto" : s.clicked_at ? "🔥 cliccata" : s.opened_at ? "👀 aperta" : "· in attesa"}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Card chirurgica follow-up: bozza editabile + copilota AI + invio singolo ──
function FollowupCard({ p, authHeader, fmtDate, isDue, onSent, checked, onToggleCheck }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(p.draft?.subject || "");
  const [text, setText] = useState(p.draft?.text || "");
  const [hasDraft, setHasDraft] = useState(!!p.draft);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachScheda, setAttachScheda] = useState(false);

  const genDraft = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/outreach/generate/${p.id}?variant=followup_1`, {}, authHeader);
      setSubject(data.subject); setText(data.text); setHasDraft(true);
      toast.success("Bozza follow-up generata");
    } catch { toast.error("Errore generazione bozza"); }
    setBusy(false);
  };

  const refine = async () => {
    if (!instruction.trim()) { toast.error("Scrivi un'istruzione per l'AI"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/refine/${p.id}`,
        { instruction, subject, text }, authHeader);
      setSubject(data.subject); setText(data.text);
      setInstruction("");
      toast.success("Bozza riscritta dal copilota ✨");
    } catch { toast.error("Errore copilota AI"); }
    setBusy(false);
  };

  const sendNow = async () => {
    if (!window.confirm(`Inviare ORA il follow-up REALE a ${p.business_name} (${p.to})?${attachScheda ? "\n\n📎 Con allegato: Schede Tecniche Annata 2025 (PDF)" : ""}`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/send/${p.id}`,
        { dry_run: false, subject, text, attach_scheda: attachScheda }, authHeader);
      if (data.sent) { toast.success(`Follow-up inviato a ${p.business_name} 🚀`); onSent(); }
      else toast.error("Invio non riuscito (Resend)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio");
    }
    setBusy(false);
  };

  return (
    <div className="border border-[#161616] bg-[#0d0d0d]" data-testid={`followup-card-${p.id}`}>
      <div className="w-full px-5 py-3.5 flex items-center gap-4">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={onToggleCheck}
          onClick={(e) => e.stopPropagation()}
          className="accent-[#C4A671] w-4 h-4 shrink-0 cursor-pointer"
          data-testid={`followup-check-${p.id}`}
        />
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 text-left flex items-center justify-between gap-4 flex-wrap hover:opacity-90 transition"
          data-testid={`followup-card-toggle-${p.id}`}
        >
          <div className="min-w-0">
            <div className="font-serif-display text-[15px]">
              {p.business_name}
              <span className={`ml-2 overline text-[8px] ${p.step === 1 ? "text-[#7A8FA6]" : "text-[#9FBF8F]"}`}>
                {p.step === 1 ? "❄️ Rilancio Outreach · 15 gg" : "👀 Sollecito Anteprima 2025 · 8 gg"}
              </span>
              {p.keyword === "concierge-b2b" && (
                <span className="ml-2 overline text-[8px] px-1.5 py-0.5 border border-[#7CB87C]/60 text-[#7CB87C]" data-testid={`followup-badge-concierge-${p.id}`}>
                  💬 Concierge B2B
                </span>
              )}
              {p.keyword === "concierge-b2b" && (
                <span className="ml-2 overline text-[8px] px-1.5 py-0.5 border border-[#7CB87C]/60 text-[#7CB87C]" data-testid={`followup-badge-concierge-${p.id}`}>
                  💬 Concierge B2B
                </span>
              )}
              {p.country && p.country !== "IT" && (
                <span className="ml-2 overline text-[8px] text-[#C4A671]">🌍 {p.country}</span>
              )}
              {hasDraft && (
                <span className="ml-2 overline text-[8px] text-[#7CB87C]">✍ bozza pronta</span>
              )}
            </div>
            <div className="text-[12px] text-[#7A7568] mt-0.5">
              {p.city || "—"} · a: {p.to} · prima email: {fmtDate(p.first_sent_at)}
            </div>
          </div>
          <span className={`text-[11px] shrink-0 ${isDue(p.due_at) ? "text-[#E0B45A]" : "text-[#7A7568]"}`}>
            {isDue(p.due_at) ? "⚡ pronto" : `previsto il ${fmtDate(p.due_at)}`} {open ? "▾" : "▸"}
          </span>
        </button>
      </div>

      {open && (
        <div className="px-5 pb-5 border-t border-[#161616] pt-4 space-y-4">
          {!hasDraft ? (
            <button
              data-testid={`followup-gen-${p.id}`}
              onClick={genDraft}
              disabled={busy}
              className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#C4A671]/60 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
            >
              {busy ? "Il copilota scrive…" : "✎ Genera bozza follow-up"}
            </button>
          ) : (
            <>
              <div>
                <label className="overline text-[9px] text-[#7A7568] block mb-1.5">Oggetto</label>
                <input
                  data-testid={`followup-subject-${p.id}`}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[14px] text-[#F2EFE8]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="overline text-[9px] text-[#7A7568]">Testo — modificabile a mano</label>
                  <button
                    type="button"
                    data-testid={`followup-listino-${p.id}`}
                    onClick={() => setText((t) => `${t.trimEnd()}\n\nListino Prezzi Ufficiale 2026: https://www.lavalledegliiblei.it/docs/Listino_Prezzi_2026_La_Valle_degli_Iblei.pdf`)}
                    className="overline text-[8.5px] px-2 py-1 border border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
                  >
                    📄 Inserisci Listino 2026
                  </button>
                </div>
                <textarea
                  data-testid={`followup-text-${p.id}`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={12}
                  className="w-full bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[13.5px] leading-[1.7] text-[#E7E2D5] font-light resize-y"
                />
              </div>

              {/* Copilota AI */}
              <div className="border border-[#C4A671]/25 bg-[#C4A671]/5 p-3.5">
                <label className="overline text-[9px] text-[#C4A671] block mb-2">
                  ✨ Copilota AI — dai un'istruzione e rigenera questa bozza
                </label>
                <div className="flex gap-2 flex-wrap">
                  <input
                    data-testid={`followup-instruction-${p.id}`}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !busy && refine()}
                    placeholder='es. "Rendila più esclusiva" · "Traduci in inglese" · "Cita l&apos;imbottigliamento della prossima settimana"'
                    className="flex-1 min-w-[240px] bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[13px] text-[#F2EFE8]"
                  />
                  <button
                    data-testid={`followup-refine-${p.id}`}
                    onClick={refine}
                    disabled={busy}
                    className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#C4A671] text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
                  >
                    {busy ? "…" : "✨ Rigenera"}
                  </button>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {["Rendila più esclusiva", "Più calda e personale", "Traduci in inglese", "Copy del Sommelier Club"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInstruction(s)}
                      className="text-[10px] px-2.5 py-1 border border-[#2a2a2a] text-[#7A7568] hover:text-[#C4A671] hover:border-[#C4A671]/50 transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer select-none text-[12px] text-[#C9C3B5]" data-testid={`followup-attach-toggle-${p.id}`}>
                <input
                  type="checkbox"
                  checked={attachScheda}
                  onChange={(e) => setAttachScheda(e.target.checked)}
                  className="accent-[#C4A671] w-4 h-4"
                  data-testid={`followup-attach-checkbox-${p.id}`}
                />
                📎 Allega <span className="text-[#C4A671]">Schede Tecniche Annata 2025</span> (PDF)
                <a href="/docs/Schede_Tecniche_Annata_2025_La_Valle_degli_Iblei.pdf" target="_blank" rel="noreferrer" className="text-[#7A7568] underline ml-1">anteprima ↗</a>
              </label>
              <div className="flex gap-3 flex-wrap">
                <button
                  data-testid={`followup-send-now-${p.id}`}
                  onClick={sendNow}
                  disabled={busy}
                  className="px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] bg-[#C4A671] text-[#0A0A0A] hover:bg-[#E0C48A] transition disabled:opacity-40"
                >
                  🚀 Invia ora{attachScheda ? " + 📎" : ""}
                </button>
                <button
                  data-testid={`followup-regen-${p.id}`}
                  onClick={genDraft}
                  disabled={busy}
                  className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#2a2a2a] text-[#C9C3B5] hover:border-[#C4A671]/60 transition disabled:opacity-40"
                >
                  ↻ Nuova bozza da zero
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Editor bozza con copilota AI (riusato in Approvazione Veloce) ──
function DraftEditor({ prospectId, initialSubject, initialText, authHeader, onUpdated }) {
  const [subject, setSubject] = useState(initialSubject);
  const [text, setText] = useState(initialText);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/draft/${prospectId}`,
        { subject, text }, authHeader);
      onUpdated({ subject: data.subject, text: data.text });
      setDirty(false);
      toast.success("Modifiche salvate — verranno usate all'invio 💾");
    } catch { toast.error("Errore salvataggio bozza"); }
    setBusy(false);
  };

  const refine = async () => {
    if (!instruction.trim()) { toast.error("Scrivi un'istruzione per l'AI"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/outreach/refine/${prospectId}`,
        { instruction, subject, text }, authHeader);
      setSubject(data.subject); setText(data.text); setDirty(false);
      onUpdated({ subject: data.subject, text: data.text });
      setInstruction("");
      toast.success("Bozza riscritta dal copilota ✨");
    } catch { toast.error("Errore copilota AI"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid={`draft-editor-${prospectId}`}>
      <div>
        <label className="overline text-[9px] text-[#7A7568] block mb-1.5">Oggetto</label>
        <input
          data-testid={`draft-subject-${prospectId}`}
          value={subject}
          onChange={(e) => { setSubject(e.target.value); setDirty(true); }}
          className="w-full bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[14px] text-[#F2EFE8]"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="overline text-[9px] text-[#7A7568]">Testo — modificabile a mano</label>
          <button
            type="button"
            data-testid={`draft-listino-${prospectId}`}
            onClick={() => { setText((t) => `${t.trimEnd()}\n\nListino Prezzi Ufficiale 2026: https://www.lavalledegliiblei.it/docs/Listino_Prezzi_2026_La_Valle_degli_Iblei.pdf`); setDirty(true); }}
            className="overline text-[8.5px] px-2 py-1 border border-[#C4A671]/50 text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition"
          >
            📄 Inserisci Listino 2026
          </button>
        </div>
        <textarea
          data-testid={`draft-text-${prospectId}`}
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          rows={12}
          className="w-full bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[13.5px] leading-[1.7] text-[#E7E2D5] font-light resize-y"
        />
      </div>

      <div className="border border-[#C4A671]/25 bg-[#C4A671]/5 p-3.5">
        <label className="overline text-[9px] text-[#C4A671] block mb-2">
          ✨ Copilota AI — dai un'istruzione e rigenera questa bozza
        </label>
        <div className="flex gap-2 flex-wrap">
          <input
            data-testid={`draft-instruction-${prospectId}`}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && refine()}
            placeholder='es. "Rendila più esclusiva" · "Traduci in inglese" · "Più calda e personale"'
            className="flex-1 min-w-[240px] bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[13px] text-[#F2EFE8]"
          />
          <button
            data-testid={`draft-refine-${prospectId}`}
            onClick={refine}
            disabled={busy}
            className="px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] border border-[#C4A671] text-[#C4A671] hover:bg-[#C4A671] hover:text-[#0A0A0A] transition disabled:opacity-40"
          >
            {busy ? "…" : "✨ Rigenera"}
          </button>
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {["Rendila più esclusiva", "Più calda e personale", "Traduci in inglese", "Copy del Sommelier Club"].map((s) => (
            <button
              key={s}
              onClick={() => setInstruction(s)}
              className="text-[10px] px-2.5 py-1 border border-[#2a2a2a] text-[#7A7568] hover:text-[#C4A671] hover:border-[#C4A671]/50 transition"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid={`draft-save-${prospectId}`}
        onClick={save}
        disabled={busy || !dirty}
        className="px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] bg-[#C4A671] text-[#0A0A0A] hover:bg-[#E0C48A] transition disabled:opacity-40"
      >
        💾 Salva modifiche {dirty && "•"}
      </button>
    </div>
  );
}

// ══════════════════ RICERCA MANUALE / SOCIAL (il limbo dei prospect) ══════════════════
function ManualSearchPanel({ authHeader }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/outreach/prospects?no_email=true&limit=500`, authHeader);
      setItems(data);
    } catch { toast.error("Impossibile caricare la lista"); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveEmail = async (p) => {
    const email = (emails[p.id] || "").trim();
    if (!email || !email.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    setBusyId(p.id);
    try {
      await axios.post(`${API}/admin/outreach/prospects/${p.id}/set-email`, { email }, authHeader);
      toast.success(`${p.business_name}: bozza in generazione → la troverai in Approvazione Veloce ✨`);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio email");
    }
    setBusyId(null);
  };

  const recheck = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/outreach/re-enrich-missing`, {}, authHeader);
      toast.success(data.already_running
        ? "Caccia email già in corso…"
        : `Caccia automatica avviata su ${data.rechecking} siti: chi ha l'email passa da solo in Approvazione Veloce ✨`);
    } catch { toast.error("Errore avvio ricontrollo"); }
  };

  const searchUrl = (p) =>
    `https://www.google.com/search?q=${encodeURIComponent(`${p.business_name} ${p.city || ""} instagram email contatti`)}`;

  return (
    <div className="max-w-5xl" data-testid="manual-search-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-serif-display italic text-2xl mb-1">Ricerca Manuale / Social</h2>
          <p className="text-[13px] text-[#8B7A55]">
            I prospect senza email si fermano qui, senza mai bloccare l'Autopilota. Trovi il contatto
            (sito, Instagram, Google), lo incolli e la macchina riparte da sola: bozza generata
            automaticamente e scheda spostata in Approvazione Veloce. Ogni 24 ore la macchina
            riprova da sola a cacciare le email mancanti su tutti i siti.
          </p>
        </div>
        <button
          data-testid="recheck-missing-emails"
          onClick={recheck}
          className="border border-[#E06C5A]/60 text-[#E06C5A] px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#E06C5A] hover:text-[#0A0A0A] transition"
        >
          🪄 Caccia automatica email
        </button>
      </div>

      {loading ? (
        <div className="text-[#7A7568] text-sm py-8">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-[#1f1f1f] p-10 text-center text-[#7A7568] text-sm">
          Limbo vuoto: tutti i contatti hanno un'email. 🎉
        </div>
      ) : (
        <div className="space-y-1" data-testid="manual-search-list">
          <div className="overline text-[10px] text-[#E06C5A] mb-2">
            📭 {items.length} contatti in attesa di email
          </div>
          {items.map((p) => (
            <div key={p.id} className="border border-[#161616] bg-[#0d0d0d] px-5 py-4" data-testid={`manual-row-${p.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif-display text-[15px]">
                    {p.business_name}
                    {p.country && p.country !== "IT" && (
                      <span className="ml-2 overline text-[8px] text-[#C4A671]">🌍 {p.country}</span>
                    )}
                  </div>
                  <div className="text-[11.5px] text-[#7A7568] mt-1 flex gap-3 flex-wrap">
                    <span>{p.city || "—"}</span>
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer" className="text-[#C4A671] underline">sito ↗</a>
                    )}
                    {p.instagram && (
                      <a href={p.instagram} target="_blank" rel="noreferrer" className="text-[#C4A671] underline">instagram ↗</a>
                    )}
                    {p.facebook && (
                      <a href={p.facebook} target="_blank" rel="noreferrer" className="text-[#C4A671] underline">facebook ↗</a>
                    )}
                    <a href={searchUrl(p)} target="_blank" rel="noreferrer" className="text-[#7A7568] underline">cerca su Google ↗</a>
                  </div>
                  {p.fit_reason && (
                    <div className="text-[11px] italic text-[#8B7A55] mt-1.5" data-testid={`manual-note-${p.id}`}>
                      🤖 {p.fit_reason}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    data-testid={`manual-email-${p.id}`}
                    value={emails[p.id] || ""}
                    onChange={(e) => setEmails({ ...emails, [p.id]: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && saveEmail(p)}
                    placeholder="email trovata sui social…"
                    className="w-[240px] bg-[#111] border border-[#2a2a2a] focus:border-[#C4A671]/60 outline-none px-3 py-2.5 text-[13px] text-[#F2EFE8]"
                  />
                  <button
                    data-testid={`manual-save-${p.id}`}
                    onClick={() => saveEmail(p)}
                    disabled={busyId === p.id}
                    className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] bg-[#C4A671] text-[#0A0A0A] hover:bg-[#E0C48A] transition disabled:opacity-40"
                  >
                    💾 Salva → Approvazione
                  </button>
                  <button
                    data-testid={`manual-discard-${p.id}`}
                    onClick={async () => {
                      if (!window.confirm(`Scartare "${p.business_name}"? Finirà nel tab Concluse dell'Archivio (recuperabile).`)) return;
                      try {
                        await axios.post(`${API}/admin/outreach/bulk-discard`, { ids: [p.id] }, authHeader);
                        toast.success(`${p.business_name} scartato → Archivio · Concluse`);
                        setItems((prev) => prev.filter((x) => x.id !== p.id));
                      } catch { toast.error("Scarto non riuscito"); }
                    }}
                    className="px-3 py-2.5 text-[10px] uppercase tracking-[0.16em] border border-[#2a2a2a] text-[#7A7568] hover:text-[#e07b6d] hover:border-[#c0392b]/50 transition"
                  >
                    ✕ Scarta
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
